'use client';

import { ElementType, useContext, useEffect, useMemo, useState } from 'react';

import { JourneyType, ProductType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import { PageHeader, ProductList } from '@bytel/product-wall';
import { ProductInterface } from '@bytel/product-wall/types';
import {
    Container,
    Pagination,
    Section,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TrilogyColor,
} from '@bytel/trilogy-react';

import { getHotspotCurrentPlan } from '@services/products';
import { wallService } from '@services/products/wall';

import { WallConfigType, WallType } from '@app-types/config';
import { SearchParamsType } from '@app-types/page';
import { MobilePlanDetailWithProvenanceType } from '@app-types/plan';
import { Product } from '@app-types/product';

import { LinkCustom } from '@components/link-custom';
import { ProductDetailsModal } from '@components/product-details-modal';
import { EmptyProductList } from '@components/walls';

import { replaceWindowUrl } from '@helpers/document';
import { findProduct, getDisplayProducts } from '@helpers/product';
import { addSearchParams, generateHref, generateTitle } from '@helpers/seo';
import { sendPageViewTracking } from '@helpers/tracking';
import { sendPageChangeTracking, sendPriceDetailTracking } from '@helpers/walls';

import { productsSkeleton } from '@constants/skeletons';

import { Breadcrumb, PlanSubtitle } from '@product-wall/components';
import { useQuery } from '@tanstack/react-query';
import { PaginationProps } from '@trilogy-ds/react/lib/components/pagination/PaginationProps';

interface HotspotWallProps {
    title: string;
    products: Product[];
    count: number;
    startPage: number;
    pageSize?: number;
    wallConfig: WallConfigType;
    searchParams?: SearchParamsType;
    plan?: MobilePlanDetailWithProvenanceType;
    trackingData?: {
        page: string;
        pageCategory: string;
        marketLine?: string;
        isB2B?: boolean;
        eCommerceStep?: string;
        variant?: Record<string, string | boolean>;
    };
    imageAs?: ElementType;
}

const activeFilters = {};

export function HotspotWall({
    title,
    wallConfig,
    products: _products,
    startPage,
    pageSize = 30,
    count: _count,
    searchParams: _searchParams,
    trackingData,
    imageAs: Image,
    plan: _plan,
}: Readonly<HotspotWallProps>) {
    const { cart } = useContext(CartContext);

    const [currentPage, setCurrentPage] = useState(startPage);
    const [searchParams, setSearchParams] = useState<URLSearchParams>(
        _searchParams ? new URLSearchParams(_searchParams) : new URLSearchParams(),
    );
    const [hasToScroll, setHasToScroll] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();

    const cartJourney = useMemo(() => {
        return cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION);
    }, [cart]);

    const cartPlan = useMemo(() => {
        return cartJourney?.getProductsByType(ProductType.MAIN_MOBILE_OFFER)?.[0];
    }, [cartJourney]);

    const currentPlanQuery = useQuery({
        queryKey: ['hotspot-current-plan', cartPlan, searchParams],
        queryFn: () => getHotspotCurrentPlan(searchParams, cartPlan),
        initialData: _plan,
    });

    const onChangePage: PaginationProps['onClick'] = async (event) => {
        event?.preventDefault();
        setCurrentPage(event?.currentPage);
        const newSearchParams = addSearchParams('page', event?.currentPage.toString(), searchParams);
        sendPageChangeTracking(false);

        setSearchParams(newSearchParams);
        setHasToScroll((prev) => prev + 1);
        replaceWindowUrl(generateHref(wallConfig, activeFilters, newSearchParams, true));
    };

    const onSelectProduct = ({ id }: ProductInterface) => {
        const product = findProduct(productsQuery.data.products, id);
        setSelectedProduct(product);

        sendPriceDetailTracking(false, WallType.HOTSPOT);
    };

    const resetFilters = () => {
        setCurrentPage(1);
    };

    const productsQuery = useQuery({
        queryKey: ['hotspot-products', currentPage, pageSize, currentPlanQuery.data?.plan],
        queryFn: () =>
            wallService
                .getHotspotProducts({
                    sort: 'meilleures-ventes',
                    page: currentPage,
                    limit: pageSize,
                    plan: currentPlanQuery.data?.plan?.gencode ?? (currentPlanQuery.data?.plan as undefined | null),
                })
                .then(({ products, count }) => ({
                    products: getDisplayProducts(products, currentPlanQuery.data),
                    count,
                })),
        initialData: { products: _products, count: _count },
    });

    const scrollToTop = () => {
        window.document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (trackingData) {
            sendPageViewTracking(false, trackingData);
        }
    }, []);

    useEffect(() => {
        if (hasToScroll) {
            scrollToTop();
        }
    }, [hasToScroll]);

    return (
        <>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <PageHeader title={generateTitle(title, undefined, { wallConfig, activeFilters })} />
            </Section>
            <Section id='products' backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <PlanSubtitle planWithProvenance={currentPlanQuery.data} />
                <Text
                    level={TextLevels.THREE}
                    aria-live='polite'
                    aria-atomic='true'
                    accessibilityLabel={`${productsQuery.data.count} ${wallConfig.productType.toLowerCase()}${productsQuery.data.count > 1 ? 's' : ''} disponible${productsQuery.data.count > 1 ? 's' : ''}`}
                >
                    {wallConfig.productType}
                    {productsQuery.data.count > 1 ? 's' : ''} disponible
                    {productsQuery.data.count > 1 ? 's' : ''} ({productsQuery.data.count})
                </Text>
                <ProductList
                    isLoading={productsQuery.isPending}
                    products={productsQuery.isPending ? productsSkeleton : productsQuery.data.products}
                    imageAs={productsQuery.isPending ? 'img' : Image}
                    firstElementIndex={(currentPage - 1) * pageSize}
                    linkAs={LinkCustom}
                    openPriceDetail={onSelectProduct}
                    data-cy='product-list'
                />
                {productsQuery.data.count <= 0 && !productsQuery.isPending && (
                    <EmptyProductList resetFilters={resetFilters} />
                )}
                <Spacer size={SpacerSize.FIVE} />
                {pageSize < productsQuery.data.count && (
                    <Pagination
                        onClick={onChangePage}
                        length={Math.ceil(productsQuery.data.count / pageSize)}
                        defaultPage={currentPage}
                        href={(page) =>
                            page < 1 || page > Math.ceil(productsQuery.data.count / pageSize)
                                ? ''
                                : `?${addSearchParams('page', page.toString(), searchParams).toString()}`
                        }
                    />
                )}
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Breadcrumb wallConfig={wallConfig} activeFilters={activeFilters} />
                </Container>
            </Section>
            {selectedProduct && (
                <ProductDetailsModal
                    currentProduct={selectedProduct}
                    onClose={() => setSelectedProduct(undefined)}
                    type={WallType.HOTSPOT}
                    plan={currentPlanQuery.data?.plan ?? undefined}
                    modalOpen={true}
                />
            )}
        </>
    );
}
